import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux'
import { Dropdown } from 'react-bootstrap'
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table'
import _ from 'lodash'
import { RiAlertFill } from 'react-icons/ri'
import { useTranslation } from 'react-i18next'

import CustomButton from '../../CustomButton'
import ModalDeviceAuditLog from '../modals/ModalDeviceAuditLog'
import ModalDeviceConfigAuditLog from '../modals/ModalDeviceConfigAuditLog'
import ModalChannelStatus from '../modals/ModalChannelStatus'
import CustomOverlayTrigger from '../../CustomOverlayTrigger'
import MobileAssetCard from './MobileAssetCard'
import MobileEventPagination from './MobileEventPagination'
import { isMobileOnly } from 'react-device-detect'
import { downloadMobileCSV } from '../../helpers/mobileCSVExport'
import '../../common/MobileGridCards.sass'

import { formatChannelStatus, formatChannelStatusCSV, channelStatusSortFunc, formatDataUsageCSV, formatStreamDataUsage, formatGroup, formatOnlineStatusLocal, formatOnlineStatusCSVLocal, formatTimestamp, formatVehicleName } from '../../helpers/tablesFuncHelpers'
import { tableExportOptions } from '../../helpers/table'
import { filterIcon } from '../../helpers/iconHelpers'
import { hasSleepStatusesAccess } from '../../helpers/helperSleepMode'
import { isInternalUser } from "../../helpers/constants"
import IconTooltip from '../../dump/menuComponents/IconTooltip'

import '../../dump/menuComponents/menuComponents.sass'

const ManageAssetsContainer = forwardRef((props, ref) => {
	const tableRef = useRef();
	const navigate = useNavigate();
	const { t } = useTranslation('translation', { keyPrefix: 'manageVehiclesPage.manageAssetsContainer' });
	const isMobile = isMobileOnly || window.innerWidth <= 991;
	const { initialAssets, devices, selectedAssets, filterColumn, filterConfigColumn, selectProfile, sortAssetsName, sortAssetsOrder, setAssetsSort, columns, getCompanyDevices, monthlyDataAvailable, selectAssets } = props;
	const { company } = useSelector(state => state.company);
	const { user } = useSelector(state => state.user);
	const userRole = user.roles[0];

	const [activeDevice, setActiveDevice] = useState(null);
	const [activeChannel, setActiveChannel] = useState(null);
	const [showModalDeviceAuditLog, setShowModalDeviceAuditLog] = useState(false);
	const [showModalDeviceConfigAuditLog, setShowModalDeviceConfigAuditLog] = useState(false);
	const [showModalChannelStatus, setShowModalChannelStatus] = useState(false);
	const [groups, setGroups] = useState([]);
	const [sortGroupNames, setSortGroupNames] = useState(true);
	const MOBILE_PAGE_SIZE = 10;
	const [mobilePage, setMobilePage] = useState(1);

	useEffect(() => {
		setMobilePage(1);
	}, [devices]);

	useEffect(() => {
		const initialGroups = initialAssets.reduce((totalGroups, { group_name }) => {
			if (group_name) {
				!totalGroups.includes(group_name) && totalGroups.push(group_name)
			} else {
				!totalGroups.includes('No group') && totalGroups.push('No group')
			}
			return totalGroups.sort()
		}, []);

		setGroups(initialGroups);
	}, [initialAssets]);

	useImperativeHandle(ref, () => ({
		exportToCSV: () => {
			if (tableRef.current) {
				tableRef.current.handleExportCSV();
			} else {
				exportMobileCSV();
			}
		},
	}));

	const exportMobileCSV = () => {
		const columnDefs = [
			{ header: t('labelAssetName'), value: (row) => formatVehicleName(row.vehicle_name, row) },
			{ header: t('labelDeviceID'), value: (row) => row.device_id },
			{ header: t('labelGroupName'), value: (row) => row.group_name },
			{ header: t('labelActivityStatus'), value: (row) => formatOnlineStatusCSVLocal(row.status, row) },
			{ header: t('labelFirstConnected'), value: (row) => row.activated_timestamp ? formatTimestamp(`${row.activated_timestamp}+00`, true) : '' },
			{ header: t('labelConfigProfile'), value: (row) => formatProfileName(row.uploaded_company_profile, row, true) },
			{ header: t('labelCameraStatus'), value: (row) => formatChannelStatusCSV(row.cameraError, row) },
			{ header: t('labelDataUsage'), value: (row) => formatDataUsageCSV(row.data_usage, row, props.companyData) },
			{ header: t('labelSDWearLevel'), value: (row) => formatSDWearLevelCSV(row.sd_wear) },
			{ header: t('labelLiveStreamDataUsage'), value: (row) => formatStreamDataUsage(row.data_usage_live_stream, row, true) },
			{ header: t('labelVIN'), value: (row) => row.vin_number },
			{ header: t('labelLicensePlateNumber'), value: (row) => row.tag_plate },
			{ header: t('labelIMEI'), value: (row) => row.serial_number },
			{ header: t('labelSDCardRemoval'), value: (row) => row.no_sd_card },
		];

		downloadMobileCSV(columnDefs, devices, t('csvFileName'));
	};

	const editVehicle = (e, id) => {
		e.preventDefault();
		navigate('/managevehicles/edit', {
			state: { id, company, devices: devices.map(e => (e = e.vehicle_name)) }
		});
	}

	const auditLogFormatter = (e, device) => {
		return (
			<CustomButton
				variant="link"
				onClick={() => {
					setShowModalDeviceAuditLog(true)
					setActiveDevice(device)
				}}
			>
				{t('btnView')}
			</CustomButton>
		)
	}

	const handleFailureChannelClick = (row, channel) => {
		setShowModalChannelStatus(true)
		setActiveDevice(row)
		setActiveChannel(channel)
	}

	const formatRecordingStatus = (cell) => {
		if (!cell) {
			return <span>OK</span>
		}
		return <span className="error-label">{t('labelNeedsReformat')}</span>
	}

	const formatRecordingStatusCSV = (cell) => (!cell ? t('labelOK') : t('labelNeedsReformat'))

	const configAuditLogFormatter = (e, device) => {
		return (
			<CustomButton
				variant="link"
				onClick={() => {
					setShowModalDeviceConfigAuditLog(true)
					setActiveDevice(device)
				}}
			>
				{t('btnViewLog')}
			</CustomButton>
		)
	}

	const { channelStatus, group, configProfile, onlineStatus, totalDataUsage, streamDataUsage, liveStream, recordingStatus, /* firmwareStatus, */ firstConnected, id, auditLog, vinNumber, plateNumber, imei, no_sd_card, sdcard_wear_level, configAuditLog } = columns;

	const assetNameCheckboxFormatter = (cell, row) => {
		return (
			<div className="camera-name-wrapper">
				<input
					type="checkbox"
					value={row.vehicle_name || row.device_id}
					checked={selectedAssets.includes(row.device_id)}
					onChange={() => selectAssets(row)}
					disabled={!row.activated_timestamp}
					className="device-checkbox"
				/>
				<CustomButton variant="link" onClick={(e) => editVehicle(e, row.device_id)}>{cell || t('btnUnnamed')}</CustomButton>
			</div>
		)
	}

	const formatProfileName = (cell, row, isCSV = false) => {
		const { config_checksum, pending_company_profile, uploaded_company_profile } = row
		const canViewProfiles = ['system_admin', 'customer_service', 'partner', 'fleet_manager', 'sales_demo', 'storage_manager'].includes(userRole)
			|| (['custom_user', 'custom_partner'].includes(userRole) && _.find(user.permissions, { perm_category_id: 3, a_view: true }))
		if (config_checksum === 1) {
			if (pending_company_profile && pending_company_profile.toLowerCase() !== 'manual') {
				return canViewProfiles && !isCSV
					? <span>{t('textPendingUpdate')} <CustomButton variant="link" name={pending_company_profile} onClick={(e) => selectProfile(e, row)}>{pending_company_profile}</CustomButton></span>
					: `${t('textPendingUpdate')} ${pending_company_profile}`
			}
			return `${t('textPendingUpdate')} ${t('textManual')}`
		}
		if (uploaded_company_profile) {
			return uploaded_company_profile === 'Manual'
				? t('textManual')
				: canViewProfiles && !isCSV
					? <CustomButton variant="link" name={uploaded_company_profile} onClick={(e) => selectProfile(e, row)}>{uploaded_company_profile}</CustomButton>
					: uploaded_company_profile
		}

		return t('textManual')
	}

	const filterValues = [...new Set(initialAssets.map(d => {
		if (d && d.config_checksum === 1) {
			return t('textPending')
		}
		if (d && d.uploaded_company_profile) {
			return d.uploaded_company_profile
		}
		return t('textManual')
	}))];

	const configProfileFilter = () => {
		let filterList = [];
		for (const value of filterValues.sort()) {
			if (value) {
				filterList.push(
					<li
						key={value}
						title={value}
					>
						<input type="checkbox" checked={isConfigChecked(value)} onChange={(e) => filterConfigColumn(e, value)} />
						<span className="groupName">{value || t('textManual')}</span>
					</li>
				);
			}
		}
		return filterList;
	}

	const isConfigChecked = (value) => {
		let devicesUpdate = devices;
		if (value === 'Manual') {
			return devicesUpdate.filter(e => e['config_checksum'] !== 1 && (e['uploaded_company_profile'] === 'Manual' || !e['uploaded_company_profile'])).length;;
		} else if (value === 'Pending') {
			return devicesUpdate.filter(e => e['config_checksum'] === 1).length;
		}
		return devicesUpdate.filter(e => e['uploaded_company_profile'] === value).length;
	}

	const numberOfSelectedDevices = () => {
		let devicesUpdate = devices;
		let i = 0;
		devicesUpdate.map(device => { if (!!device.activated_timestamp) i++ });
		return i;
	}

	const handleSortGroupsClick = (sortGroupNames) => {
		setSortGroupNames(sortGroupNames);
		let data = groups;
		sortGroupNames ? setGroups(data.sort()) : setGroups(data.sort().reverse());
	}

	const formatDataUsageLocal = (cell, row) => {
		const deviceDataUsageGB = row.data_usage / 1024 / 1024 / 1024
		const deviceDataUsagePerc = (deviceDataUsageGB * 100 / monthlyDataAvailable).toFixed(1)
		const isDeviceDataUsageOver = (deviceDataUsageGB >= monthlyDataAvailable * 0.3)

		return (
			<div className="device-data-usage">
				{deviceDataUsageGB.toFixed(2)}
				{isDeviceDataUsageOver &&
					<CustomOverlayTrigger
						{...{ type: 'warn', title: t('overlayTriggerWarnTitle'), body: t('overlayTriggerWarnBody', { deviceDataUsagePerc }) }}
						placement="top"
						trigger="click"
						closeBtn={true}
						prefix='warn'
						bodyPrefix='text-center'
					>
						<div>
							<CustomButton variant="link">
								<RiAlertFill />
							</CustomButton>
						</div>
					</CustomOverlayTrigger>
				}
			</div>
		)
	}

	const formatSDWearLevel = (cell) => {
		if (cell >= 0 && cell != null) {
			let SDWearLevel, color;
			if (cell === 0) {
				SDWearLevel = t('labelEndOfLife');
				color = '#E83219';
			} else if (cell > 25) {
				SDWearLevel = t('labelHealthy');
				color = '#359D0F';
			} else if (10 <= cell && cell <= 25) {
				SDWearLevel = t('labelMonitor');
				color = '#0071B6';
			} else if (cell < 10) {
				SDWearLevel = t('labelReplaceSoon');
				color = '#FF9900';
			}

			return <span className="sdWearLevelLabel" style={{ backgroundColor: color }}>{SDWearLevel}</span>
		} else {
			return t('textUnavailable')
		}
	}

	const formatSDWearLevelCSV = (cell) => {
		return cell ? cell : t('textUnavailable')
	}

	// DETERMINE TABLE MIN-HEIGHT BASED ON DROPDOWNS
	// const tableHeight = `${(groups.length > filterValues.length ? groups.length : filterValues.length) * 20 + 140}px`;
	const tableHeight = '0px';

	return (
		<div className="manage-assets-table">
			{isMobile ? (
				<>
					<div className="mobile-grid-cards-container">
						{devices.slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE).map((row, i) => (
							<MobileAssetCard
								key={row.device_id || i}
								row={row}
								selectedAssets={selectedAssets}
								selectAssets={selectAssets}
								editVehicle={editVehicle}
								formatGroup={formatGroup}
								formatOnlineStatusLocal={formatOnlineStatusLocal}
								formatProfileName={formatProfileName}
								formatDataUsageLocal={formatDataUsageLocal}
								formatChannelStatus={formatChannelStatus}
								formatStreamDataUsage={formatStreamDataUsage}
								formatSDWearLevel={formatSDWearLevel}
								formatTimestamp={formatTimestamp}
								handleFailureChannelClick={handleFailureChannelClick}
								auditLogFormatter={auditLogFormatter}
								configAuditLogFormatter={configAuditLogFormatter}
							/>
						))}
					</div>
					<MobileEventPagination page={mobilePage} sizePerPage={MOBILE_PAGE_SIZE} totalSize={devices.length} onPageChange={(p) => setMobilePage(p)} />
				</>
			) : (
				<BootstrapTable
				ref={tableRef}
				data={devices}
				{...tableExportOptions}
				exportCSV={false}
				options={{
					...tableExportOptions.options,
					sortName: sortAssetsName,
					sortOrder: sortAssetsOrder,
					onSortChange: setAssetsSort,
				}}
				tableStyle={{ minHeight: '0px' }}
				tableContainerClass="rosco-table manage-vehicles-table"
				headerContainerClass="rosco-table-header manage-vehicles-table-header"
				bodyContainerClass="rosco-table-body manage-vehicles-table-body"
				csvFileName={`${t('csvFileName')}.csv`}
			>
				<TableHeaderColumn
					width="250px"
					dataField="vehicle_name"
					dataFormat={assetNameCheckboxFormatter}
					csvFormat={formatVehicleName}
					isKey
					dataSort
					csvHeader={t('labelAssetName')}
				>
					<input type="checkbox" checked={numberOfSelectedDevices() && selectedAssets.length === numberOfSelectedDevices()} onChange={(e) => props.selectAllDevices(e)} className="device-checkbox" />
					<label className="camera-name-label">{t('labelAssetName')}</label>
				</TableHeaderColumn>
				<TableHeaderColumn
					width="100px"
					dataField="device_id"
					csvHeader={t('labelDeviceID')}
					hidden={!id}
				>
					{t('labelDeviceID')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="180px"
					dataField="group_name"
					dataFormat={formatGroup}
					csvHeader={t('labelGroupName')}
					hidden={!group}
					className="with-filter"
				>
					<div className="filter-wrapper">
						<label className="filter-label">{t('labelGroupName')}</label>
						<span className="order" style={{ cursor: 'pointer', marginLeft: '4px' }}>
							{(sortAssetsName !== 'group_name' || (sortAssetsName === 'group_name' && sortAssetsOrder === 'desc')) &&
								<span className="dropdown" onClick={() => setAssetsSort('group_name', 'asc')}><span className="caret"></span></span>
							}
							{(sortAssetsName !== 'group_name' || (sortAssetsName === 'group_name' && sortAssetsOrder === 'asc')) &&
								<span className="dropup" onClick={() => setAssetsSort('group_name', 'desc')}><span className="caret"></span></span>
							}
						</span>
						<Dropdown drop id="type-filter-btn" className="filter-dropdown">
							<Dropdown.Toggle as='span' className="typeFilter" bsPrefix="no-caret">
								{filterIcon}
							</Dropdown.Toggle>
							<Dropdown.Menu className="typeFilterMenu">
								<div className="groups-header">
									<h5 className="category-title">{t('titleGroups')}</h5>
									<span className={`order ${sortGroupNames ? 'dropup' : ''}`}>
										<span className="caret" onClick={() => handleSortGroupsClick(!sortGroupNames)}></span>
									</span>
								</div>
								{groups.map(groupItem => {
									return (
										<li
											key={groupItem}
											title={groupItem}
										>
											<input
												type="checkbox"
												checked={groupItem === 'No group' ? devices.filter(e => !e.group_name).length : devices.filter(e => e.group_name === groupItem).length}
												onChange={(e) => filterColumn(e, 'group_name', groupItem)}
											/>
											<span className="groupName">{groupItem}</span>
										</li>
									)
								})}
								<div className="clear-all-container">
									<CustomButton variant="primary-outline" size="sm" prefix="clear-all" onClick={(e) => filterColumn(e, 'clear_all')}>
										{`${devices.length === 0 ? t('select') : t('clear')} ${t('all')}`}
									</CustomButton>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</TableHeaderColumn>
				<TableHeaderColumn
					width="260px"
					dataField="status"
					dataFormat={formatOnlineStatusLocal}
					csvFormat={formatOnlineStatusCSVLocal}
					csvHeader={t('labelActivityStatus')}
					className="with-filter"
					columnClassName="fleet-activity-status"
					hidden={!onlineStatus}
				>
					<div className="filter-wrapper">
						<label className="filter-label">{t('labelActivityStatus')}</label>
						<span className="order" style={{ cursor: 'pointer', marginLeft: '4px' }}>
							{(sortAssetsName !== 'status' || (sortAssetsName === 'status' && sortAssetsOrder === 'desc')) &&
								<span className="dropdown" onClick={() => setAssetsSort('status', 'asc')}><span className="caret"></span></span>
							}
							{(sortAssetsName !== 'status' || (sortAssetsName === 'status' && sortAssetsOrder === 'asc')) &&
								<span className="dropup" onClick={() => setAssetsSort('status', 'desc')}><span className="caret"></span></span>
							}
						</span>
						<Dropdown drop id="type-filter-btn" className="filter-dropdown">
							<Dropdown.Toggle as='span' bsPrefix="no-caret" className="typeFilter">
								{filterIcon}
							</Dropdown.Toggle>
							<Dropdown.Menu className="typeFilterMenu">
								<h5 className="category-title">{t('labelActivityStatus')}</h5>
								<li
									key="online"
									title={t('labelDriving')}
								>
									<input type="checkbox" checked={devices.filter(e => e.status === 'driving').length} onChange={(e) => filterColumn(e, 'status', 'driving')} />
									<span className="groupName">{t('labelDriving')}</span>
								</li>
								<li
									key="parked"
									title={t('labelParked')}
								>
									<input type="checkbox" checked={devices.filter(e => e.status === 'parked').length} onChange={(e) => filterColumn(e, 'status', 'parked')} />
									<span className="groupName">{t('labelParked')}</span>
								</li>
								<li
									key="idling"
									title={t('labelIdling')}
								>
									<input type="checkbox" checked={devices.filter(e => e.status === 'idling').length} onChange={(e) => filterColumn(e, 'status', 'idling')} />
									<span className="groupName">{t('labelIdling')}</span>
								</li>
								<li
									key="offline"
									title={t('labelOffline')}
								>
									<input type="checkbox" checked={devices.filter(e => e.status === 'offline').length} onChange={(e) => filterColumn(e, 'status', 'offline')} />
									<span className="groupName">{t('labelOffline')}</span>
								</li>
								<li
									key="disabled"
									title={t('labelDisabled')}
								>
									<input type="checkbox" checked={devices.filter(e => e.status === 'disabled').length} onChange={(e) => filterColumn(e, 'status', 'disabled')} />
									<span className="groupName">{t('labelDisabled')}</span>
								</li>
								{hasSleepStatusesAccess() === true &&
									<>
										<li
											key="sleep"
											title={t('labelSleep')}
										>
											<input type="checkbox" checked={devices.filter(e => e.status === 'sleep').length} onChange={(e) => filterColumn(e, 'status', 'sleep')} />
											<span className="groupName">{t('labelSleep')}</span>
										</li>
										<li
											key="awake"
											title={t('labelAwake')}
										>
											<input type="checkbox" checked={devices.filter(e => e.status === 'awake').length} onChange={(e) => filterColumn(e, 'status', 'awake')} />
											<span className="groupName">{t('labelAwake')}</span>
										</li>
									</>
								}
								<div className="clear-all-container">
									<CustomButton variant="primary-outline" size="sm" prefix="clear-all" onClick={(e) => filterColumn(e, 'clear_all')}>
										{`${devices.length === 0 ? t('select') : t('clear')} ${t('all')}`}
									</CustomButton>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</TableHeaderColumn>
				<TableHeaderColumn
					width="160px"
					dataField="activated_timestamp"
					dataFormat={cell => cell ? formatTimestamp(`${cell}+00`, true) : cell}
					csvFormat={cell => cell ? formatTimestamp(`${cell}+00`, true) : cell}
					csvHeader={t('labelFirstConnected')}
					hidden={!firstConnected}
				>
					{t('labelFirstConnected')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="180px"
					dataField="uploaded_company_profile"
					dataFormat={formatProfileName}
					csvFormat={(cell, row) => formatProfileName(cell, row, true)}
					csvHeader={t('labelConfigProfile')}
					className="with-filter"
					hidden={!configProfile}
				>
					<div className="filter-wrapper">
						<label className="filter-label">{t('labelSettingsProfile')}</label>
						<Dropdown drop id="type-filter-btn" className="filter-dropdown">
							<Dropdown.Toggle as='span' bsPrefix="no-caret" className="typeFilter config-filter">
								{filterIcon}
							</Dropdown.Toggle>
							<Dropdown.Menu className="typeFilterMenu">
								<h5 className="category-title">{t('labelSettingsProfile')}</h5>
								{configProfileFilter()}
								<div className="clear-all-container">
									<CustomButton variant="primary-outline" size="sm" prefix="clear-all" onClick={(e) => filterColumn(e, 'clear_all')}>
										{`${devices.length === 0 ? t('select') : t('clear')} ${t('all')}`}
									</CustomButton>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</TableHeaderColumn>
				<TableHeaderColumn
					width="160px"
					dataField="cameraError"
					dataFormat={(cell, row) => formatChannelStatus(row.channel_status, row, handleFailureChannelClick)}
					csvFormat={formatChannelStatusCSV}
					csvHeader={t('labelCameraStatus')}
					dataSort
					sortFunc={channelStatusSortFunc}
					hidden={!channelStatus}
				>
					{t('labelCameraStatus')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="160px"
					dataField="data_usage"
					dataFormat={(cell, row) => formatDataUsageLocal(cell, row)}
					csvFormat={(cell, row) => formatDataUsageCSV(cell, row, props.companyData)}
					csvHeader={t('labelDataUsage')}
					dataSort
					hidden={!totalDataUsage}
				>
					{t('labelDataUsage')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="160px"
					dataField="sd_wear"
					dataFormat={formatSDWearLevel}
					csvFormat={formatSDWearLevelCSV}
					csvHeader={t('labelSDWearLevel')}
					dataSort
					hidden={!sdcard_wear_level}
				>
					{t('labelSDWearLevel')}
					<IconTooltip
						description={t('SD_Card_Health_description')}
						placement="left"
						id="score-tooltip"
					/>
				</TableHeaderColumn>
				<TableHeaderColumn
					width="200px"
					dataField="data_usage_live_stream"
					dataFormat={(cell, row) => formatStreamDataUsage(cell, row)}
					csvFormat={(cell, row) => formatStreamDataUsage(cell, row, true)}
					csvHeader={t('labelLiveStreamDataUsage')}
					dataSort
					hidden={!streamDataUsage}
				>
					{t('labelLiveStreamDataUsage')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="200px"
					dataField="vin_number"
					csvHeader={t('labelVIN')}
					hidden={!vinNumber}
				>
					{t('labelVIN')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="200px"
					dataField="tag_plate"
					csvHeader={t('labelLicensePlateNumber')}
					hidden={!plateNumber}
				>
					{t('labelLicensePlateNumber')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="200px"
					dataField="serial_number"
					csvHeader={t('labelIMEI')}
					hidden={!imei}
				>
					{t('labelIMEI')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="200px"
					dataFormat={auditLogFormatter}
					hidden={!auditLog}
				>
					{t('labelAuditLog')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="200px"
					dataField="no_sd_card"
					hidden={!no_sd_card}
					dataSort
				>
					{t('labelSDCardRemoval')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="200px"
					dataFormat={configAuditLogFormatter}
					hidden={!configAuditLog}
				>
					{t('labelConfigAuditLog')}
				</TableHeaderColumn>
			</BootstrapTable>
			)}

			{showModalDeviceAuditLog && (
				<ModalDeviceAuditLog
					device={activeDevice}
					user_role={userRole}
					onHide={() => setShowModalDeviceAuditLog(false)}
				/>
			)}

			{showModalDeviceConfigAuditLog && (
				<ModalDeviceConfigAuditLog
					device={activeDevice}
					user_role={userRole}
					onHide={() => setShowModalDeviceConfigAuditLog(false)}
				/>
			)}

			{showModalChannelStatus && (
				<ModalChannelStatus
					device={activeDevice}
					channel={activeChannel}
					userRole={userRole}
					getCompanyDevices={getCompanyDevices}
					onHide={() => setShowModalChannelStatus(false)}
				/>
			)}
		</div>
	)
})

export default ManageAssetsContainer;
